import _ from 'lodash';
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  CPThemeApi,
  CustomerPortalTheme,
  GetThemeFromWebsitePayload,
} from 'api/customer-portal/theme-api';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';

export const getAllCpThemesKey = () => 'useGetAllCpThemes';

export const useGetAllCpThemes = () => {
  return useQuery(getAllCpThemesKey(), () => CPThemeApi.getAllThemes());
};

export const useGetCpThemeById = (id: number) => {
  return useQuery(
    ['useGetCpThemeById', id],
    () => CPThemeApi.getThemeById(id),
    { enabled: _.isFinite(id) && id > 0 },
  );
};

export const useGetAvailableGoogleFonts = () => {
  return useQuery('useGetAvailableGoogleFonts', () =>
    CPThemeApi.getAvailableFonts(),
  );
};

export const useGetVariantsForFonts = (family: string) => {
  return useQuery(
    ['useGetVariantsForFonts', family],
    () => CPThemeApi.getVariantsForFonts(family),
    { initialData: { variants: [] as string[] } },
  );
};

export const useCreateCpTheme = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: Omit<CustomerPortalTheme, 'customerPortalThemeId'>) => {
      if (!payload.customerPortalThemeName) {
        payload.customerPortalThemeName = payload?.brandNameOrWebsite ?? '';
      }

      return CPThemeApi.createTheme(payload);
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries(getAllCpThemesKey());
      },
    },
  );
};

export const useGetThemeFromWebsite = () => {
  return useMutation((payload: GetThemeFromWebsitePayload) =>
    CPThemeApi.getThemeFromWebsite(payload),
  );
};

export const useUpdateCpTheme = () => {
  return useMutation(
    (args: {
      payload: Omit<CustomerPortalTheme, 'customerPortalThemeId'>;
      id: number;
    }) => CPThemeApi.updateTheme(args),
  );
};

export const useDeleteCpThemeById = () => {
  return useMutation((id: number) => CPThemeApi.deleteThemeById(id));
};

export const useShowThemeFailureToast = () => {
  const message = useTranslation('cp.theme.failure.message');
  const title = useTranslation('cp.theme.failure.title');

  const callback = useCallback(() => {
    toastService({
      message,
      title,
    }).error();
  }, [message, title]);

  return callback;
};

export const useThemePortalsCount = (id: number | null) => {
  return useQuery(
    ['useThemePortalsCount', id],
    () => (id ? CPThemeApi.getPortalsCountForTheme(id) : undefined),
    { enabled: _.isFinite(id) && !!id && id > 0 },
  );
};
